<template>
  <div id="app">
    <div class="ddrop-processing-overlay" v-if="formSubmitting"></div>
    <div class="ddrop-formloading" v-if="questions.length === 0">
      <h4><i class="fas fa-spinner fa-spin"></i> Loading form...</h4>
    </div>

    <form v-bind:action="actionUrl" 
          ref="form"
          enctype="multipart/form-data" 
          method="post"
          v-on:submit.prevent="submitForm"
          v-bind:class="{ 'grayed-out' : formSubmitting }"
          :disabled="formSubmitting"
          >
    
    <div class="alert alert-danger" role="alert" v-if="returnmessage !== null">
      {{ returnmessage }}
    </div>

    <div class="alert alert-danger" id="error-summary" role="alert" aria-live="assertive" tabindex="-1" v-if="errors.length > 0">
      Please correct the following errors:
      <ul class="pl-3 mt-3 mb-0">
        <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
      </ul>
    </div>

    <div class="ddrop-container">
      <div class="row">
        <div class="col-12">
          <div v-for="(questionObj, index) in formQuestions" v-bind:key="index">
            <component :ref="questionObj.msqID.toString()"
                       v-bind:is="getFormComponentType(questionObj)"
                       v-bind:question="questionObj">
            </component>
          </div>

          <div class="form-group" v-if="questions.length">
            <button type="submit" class="btn btn-primary btn-block" :disabled="formSubmitting" v-html="submitButtonHtml"></button>
          </div>
        </div>
      </div>
    </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';

/* eslint-disable no-unused-vars */
import FormInput from './components/FormInput.vue'
import FormFile from './components/FormFile.vue'
import FormSelect from './components/FormSelect.vue'
import FormCheckboxList from './components/FormCheckboxList.vue'
/* eslint-enable no-unused-vars */

export default {
  name: 'App',
  data() {
    return {
      questions: [],
      errors: [],
      returnmessage: null,
      formSubmitting: false,
    }
  },

  created() {
    fetch(this.configUrl)
        .then((res) => res.json())
        .then((json) => {
          this.questions = json.questions
    })
  },

  methods: {
    // generate question name (for input elements) from a question
    makeQuestionId: function (question) {
        return 'Question' + question.msqID;
    },
    getRequiredErrorMessage(question) {
        switch (question.type) {
            case 'select':
                return `Select an option for ${question.question}.`;
            case 'checkboxlist':
                return `Select at least one option for ${question.question}.`;
            case 'file':
                return `Upload at least one file for ${question.question}.`;
            default:
                return `Enter a value for ${question.question}.`;
        }
    },
    getRecaptchaToken(action) {
        return new Promise((resolve, reject) => {
            if (!this.recaptchaSiteKey) {
                reject(new Error('Missing reCAPTCHA site key.'));
                return;
            }

            if (!window.grecaptcha) {
                reject(new Error('reCAPTCHA has not loaded.'));
                return;
            }

            window.grecaptcha.ready(() => {
                window.grecaptcha
                    .execute(this.recaptchaSiteKey, { action })
                    .then(resolve)
                    .catch(reject);
            });
        });
    },

    async submitForm() {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const form = this.$refs.form;
        const formData = new FormData(form);

        this.formSubmitting = true;
        this.errors = [];

        this.questions.forEach((question) => {

            let valueToCheck;

            switch (question.type) {
                case 'text':
                case 'textarea':
                case 'select':
                case 'email':
                    valueToCheck = question.answer;
                    break;
                case 'file':
                    valueToCheck = question.answer && question.answer.length > 0 ? question.answer : null;
                    break;
                case 'checkboxlist':
                    valueToCheck = Array.isArray(question.answer) && question.answer.length > 0 ? question.answer : null;
                    break;
                default:
                    valueToCheck = question.answer;
            }

            if (question.required && !valueToCheck) {
                question.error = this.getRequiredErrorMessage(question);
                this.errors.push(`${question.question} is required`);
            } else if (question.type === 'email' && valueToCheck && !emailRegex.test(valueToCheck)) {
                question.error = 'Enter a valid email address.';
                this.errors.push(`${question.question} must be an email address.`);
            } else {
                question.error = null;
            }

        });

        if (this.errors.length > 0) {
            this.formSubmitting = false;

            this.$nextTick(() => {
                const errorSummary = document.getElementById('error-summary');
                if (errorSummary) {
                    errorSummary.focus();
                }
            });

            return;
        }

        try {
            const recaptchaToken = await this.getRecaptchaToken(this.recaptchaPreflightAction);

            const preflightData = new FormData();
            preflightData.append('recaptchaToken', recaptchaToken);

            const preflightResponse = await axios.post(this.preflightUrl, preflightData);

            const uploadToken = preflightResponse.data.uploadToken;

            if (!uploadToken) {
                throw new Error('Missing upload token.');
            }

            formData.append('uploadToken', uploadToken);

            const response = await axios.post(this.actionUrl, formData);

            const data = response.data;
            window.location.replace(data.redirectPath);

        } catch (error) {
            if (error.response) {
                const data = error.response.data;
                this.returnmessage = data.returnmessage || 'There was a problem submitting the form.';
                this.errors = data.errors || [];
                this.processFormErrors();
            } else {
                this.returnmessage = error.message || 'There was a problem submitting the form.';
                this.errors = [this.returnmessage];
            }

            this.formSubmitting = false;

            this.$nextTick(() => {
                const errorSummary = document.getElementById('error-summary');
                if (errorSummary) {
                    errorSummary.focus();
                }
            });
        }
    },
    processFormErrors() {
      for(var qId in this.errors) {
          let question = this.questions.find(function (q) {
              return q.qID === qId;
          });
          if (question !== undefined) {
                question.error = this.errors[qId];
            } else {
                console.warn("Cannot display error for " + question + " with qID of " + qId);
          }
      }
    },
    getFormComponentType: function(question) {
        let componentType = "FormInput"
        switch(question.type) {
          case "text":
            componentType = 'FormInput';
            break;
          case "file":
            componentType = 'FormFile';
            break;
          case "checkboxlist":
            componentType = 'FormCheckboxList';
            break;
          case "select":
            componentType = 'FormSelect';
            break;
          default:
            componentType = 'FormInput';
        }
        return componentType;
    }
  },

  computed: {
    formQuestions() {
      return this.questions;
    },
    actionUrl() {
      return this.$store.state.actionurl;
    },
    preflightUrl() {
      return this.$store.state.preflighturl;
    },
    recaptchaSiteKey() {
      return this.$store.state.recaptchaSiteKey;
    },
    recaptchaPreflightAction() {
      return this.$store.state.recaptchaPreflightAction;
    },
    configUrl() {
      return this.$store.state.configurl;
    },
    bID() {
      return this.$store.state.bID;
    },
    formerrors() {
      return this.errors;
    },
    responsemessage() {
      return this.returnmessage;
    },
    submitButtonHtml() {
      return this.formSubmitting ? '<i class="fas fa-circle-notch fa-spin"></i> Processing' : 'Submit entries';
    }
  },

  mounted() {
  },

  components: {
    FormInput,
    FormFile,
    FormSelect,
    FormCheckboxList
  },
}
</script>

<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: relative;
}
.ddrop-processing-overlay {
    position:absolute;
    width:100%;
    height:100%;
    background-color: rgba($color: #ffffff, $alpha: 0.5);
    z-index: 1000;
}
.ddrop-formloading {
  min-height:200px;
}
.grayed-out {
   filter: grayscale(90%);
}
.red-alert {
    border-color: red !important;
}
.span-error {
   color:#721c24 !important;
}
.form-surname {
  position:absolute;
  left:-100000px;
}

.text-muted.small.error {
	color: red !important;
}
.has-error input,.has-error file, .has-error textarea, .has-error select {
  border: 2px solid red; 
}
.grecaptcha-badge {
  visibility: hidden;
}
</style>
